import type { BudgetZone, BudgetAdjustment } from "./adjustmentModel";
import type { Identifier, RaRecord } from "ra-core";
import type { ZoneKey, FinancePlan } from "./financeModel";
import type { Expense } from "../types";
export const EXPENSE_CATEGORIES = [
  { value: "salary", label: "Зарплата" },
  { value: "delivery", label: "Доставка" },
  { value: "purchase", label: "Закупка" },
  { value: "materials", label: "Материалы" },
  { value: "hardware", label: "Фурнитура" },
  { value: "installation", label: "Монтаж" },
  { value: "measurement", label: "Замер" },
  { value: "contractor", label: "Подрядчик" },
  { value: "transport", label: "Транспорт" },
  { value: "other", label: "Другое" },
];
export const EXPENSE_REASONS = [
  { value: "planned", label: "Плановый расход" },
  { value: "employee_error", label: "Ошибка сотрудника" },
  { value: "client_change", label: "Изменение клиента" },
  { value: "production_error", label: "Ошибка производства" },
  { value: "force_majeure", label: "Форс-мажор" },
] as const;
export type ExpenseReason = (typeof EXPENSE_REASONS)[number]["value"];
export type ExpenseReviewInput = {
  expense_id: Identifier;
  deal_id: Identifier;
  to_zone?: BudgetZone | null;
  expected_revision: number;
  decision: "approved" | "rejected";
  zone_key: ZoneKey;
  reason: ExpenseReason;
  note: string;
};
export interface ExpenseReview extends RaRecord {
  expense_snapshot?: Partial<Expense>;
  budget_adjustment_id?: Identifier | null;
  budget_to_zone?: BudgetZone | null;
  expense_id: Identifier;
  deal_id: Identifier;
  zone_key: ZoneKey | null;
  decision: "approved" | "rejected";
  amount: number;
  note: string;
  reason: ExpenseReason;
  reviewed_at: string;
  reviewed_by_sales_id: Identifier;
}
export interface ExpenseTotal extends RaRecord {
  deal_id: Identifier;
  zone_key: ZoneKey;
  approved_amount: number;
  expense_count: number;
}
export const expenseCategoryLabel = (key: string) =>
  EXPENSE_CATEGORIES.find((c) => c.value === key)?.label ?? key;
export const rubles = (value: number) =>
  new Intl.NumberFormat("ru-RU", { style: "currency", currency: "RUB" }).format(
    Number(value),
  );
export function expenseBalances(
  plan: FinancePlan,
  totals: ExpenseTotal[],
  adjustments: BudgetAdjustment[] = [],
) {
  const rows = plan.zones.map((zone) => {
    const spent = totals
      .filter(
        (t) =>
          String(t.deal_id) === String(plan.deal_id) && t.zone_key === zone.key,
      )
      .reduce((sum, t) => sum + Math.round(Number(t.approved_amount) * 100), 0);
    const change = adjustments
      .filter((a) => String(a.deal_id) === String(plan.deal_id))
      .reduce(
        (sum, a) =>
          sum +
          (a.to_zone === zone.key ? Math.round(Number(a.amount) * 100) : 0) -
          (a.from_zone === zone.key ? Math.round(Number(a.amount) * 100) : 0),
        0,
      );
    const budget = Math.round(Number(zone.budget) * 100) + change;
    return {
      key: zone.key,
      budget: budget / 100,
      spent: spent / 100,
      balance: (budget - spent) / 100,
    };
  });
  const spent = rows.reduce((sum, r) => sum + Math.round(r.spent * 100), 0);
  return {
    rows,
    spent: spent / 100,
    remainder: (Math.round(Number(plan.price) * 100) - spent) / 100,
  };
}

export type StandaloneExpenseInput = {
  deal_id: Identifier;
  amount: number;
  category: string;
  spent_at: string;
  note: string;
  zone_key: ZoneKey;
  reason: ExpenseReason;
  to_zone: BudgetZone | null;
  beneficiary_sales_id: Identifier | null;
  submission_key: string;
};
